import { IMPORT_SOURCE_OBJ } from "@signals/shared";
import { useRef, useState, type FormEvent } from "react";
import {
  BROKER_SYNC_REVIEW_ACTION_OBJ, BROKER_SYNC_REVIEW_PREPARATION_KIND_OBJ,
  type BrokerSyncIssue, type BrokerSyncReviewApplyInput, type BrokerSyncReviewApplyResponse,
  type BrokerSyncReviewPreviewInput, type BrokerSyncReviewPreviewResponse,
} from "@signals/shared";
import { Button, Modal, formatCurrency, qk, useApiMutation, useApiQuery } from "../_common";
import { describeApiError, redactKnownSecrets } from "./broker-sync-panel-error.util";

export function BrokerSyncReviewDialog({ portfolioId, issue, managementCode, onCode, onApplied, onClose }: {
  portfolioId: string;
  issue: BrokerSyncIssue;
  managementCode: string;
  onCode: (code: string) => void;
  onApplied: (result: BrokerSyncReviewApplyResponse) => void;
  onClose: () => void;
}) {
  const credential = useRef(managementCode);
  const [codeInput, setCodeInput] = useState("");
  const [snapshotDates, setSnapshotDates] = useState<string[]>([]);
  const [version, setVersion] = useState(0);
  const [preparedVersion, setPreparedVersion] = useState(-1);
  const [bindings, setBindings] = useState<Array<{ issueId: string; cashFlowId: string }>>([]);
  const snapshots = useApiQuery<{ items: Array<{ date: string }> }>(qk.portfolio.snapshots(portfolioId),
    `/api/portfolio/snapshots?${new URLSearchParams({ portfolioId })}`, { retry: false });
  const headers = () => ({ "x-mcp-management-code": credential.current });
  const prepare = useApiMutation<BrokerSyncReviewPreviewInput, BrokerSyncReviewPreviewResponse>(
    "/api/portfolio/broker-sync/review/preview", "POST", { headers, gcTime: 0, retry: false });
  const apply = useApiMutation<BrokerSyncReviewApplyInput, BrokerSyncReviewApplyResponse>(
    "/api/portfolio/broker-sync/review/apply", "POST", { headers, gcTime: 0, retry: false,
      onError: () => setPreparedVersion(-1),
      onSuccess: (result) => { if (result.applied || result.replayed) onApplied(result); } });
  const action = issue.details?.actions.find((item) => item.enabled && item.kind !== BROKER_SYNC_REVIEW_ACTION_OBJ.STATEMENT_REQUIRED);
  const kind = action?.kind === BROKER_SYNC_REVIEW_ACTION_OBJ.INCOME_SETTLEMENT
    ? BROKER_SYNC_REVIEW_PREPARATION_KIND_OBJ.INCOME_SETTLEMENTS : BROKER_SYNC_REVIEW_PREPARATION_KIND_OBJ.FEE_HISTORY;
  const preview = prepare.data;
  const current = version === preparedVersion;
  const busy = prepare.isPending || apply.isPending;
  const succeeded = Boolean(apply.data?.applied || apply.data?.replayed);
  const secrets = [credential.current, codeInput];
  const error = apply.isError ? apply.error : prepare.isError ? prepare.error : null;

  function prepareReview(event: FormEvent) {
    event.preventDefault();
    const code = codeInput || credential.current;
    if (!code || !issue.id || !action || busy) return;
    credential.current = code;
    onCode(code);
    setCodeInput("");
    setPreparedVersion(version);
    apply.reset();
    const base = { portfolioId, accountId: issue.accountId, issueIds: [issue.id], snapshotDates };
    const incomeEventId = issue.details?.alreadyAccounted.incomeEventId;
    const input: BrokerSyncReviewPreviewInput = kind === BROKER_SYNC_REVIEW_PREPARATION_KIND_OBJ.INCOME_SETTLEMENTS
      ? { ...base, kind, incomeEventIds: incomeEventId ? [incomeEventId] : [] }
      : { ...base, kind: BROKER_SYNC_REVIEW_PREPARATION_KIND_OBJ.FEE_HISTORY, ...(bindings.length ? { cashFlowBindings: bindings } : {}) };
    prepare.mutate(input);
  }

  function changeSnapshots(date: string, checked: boolean) {
    setSnapshotDates((previous) => checked ? [...previous, date].sort() : previous.filter((item) => item !== date));
    setVersion((previous) => previous + 1);
    apply.reset();
  }

  return <Modal open={true} onClose={busy ? () => {} : onClose} title="Разбор записи брокера">
    <div className="min-w-0 space-y-4 [overflow-wrap:anywhere]">
      <p>{issue.details?.reasonText ?? "Для изменения учёта нужно проверить исходные подтверждения."}</p>
      {!action || !issue.id ? <p role="status">Сейчас не хватает подтверждённых исходных данных. Финансовые изменения недоступны.</p> : <>
        <form onSubmit={prepareReview} className="space-y-3">
          {(!managementCode || error) && !succeeded && <label className="block space-y-1 text-sm">
            <span>Код управления для этого действия</span>
            <input type="password" autoComplete="current-password" value={codeInput} onChange={(event) => setCodeInput(event.target.value)}
              className="h-10 w-full rounded-md border bg-background px-3" required={!credential.current} />
          </label>}
          <details className="rounded-md border p-3">
            <summary className="cursor-pointer text-sm font-medium">Сохранённые оценки</summary>
            <p className="my-2 text-xs text-muted-foreground">Будут пересчитаны только выбранные даты. Без выбора сохранённые версии останутся прежними.</p>
            {snapshots.isLoading && <p className="text-sm">Загружаем даты оценок…</p>}
            {snapshots.isError && <p className="text-sm text-destructive">Не удалось загрузить даты. Можно продолжить без пересчёта сохранённых версий.</p>}
            {(snapshots.data?.items ?? []).map(({ date }) => <label key={date} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={snapshotDates.includes(date)} disabled={busy || succeeded ||
                kind === BROKER_SYNC_REVIEW_PREPARATION_KIND_OBJ.FEE_HISTORY && Boolean(issue.details?.operationDate && date < issue.details.operationDate)}
                onChange={(event) => changeSnapshots(date, event.target.checked)} />{date}
            </label>)}
          </details>
          <Button type="submit" disabled={busy || succeeded}>{prepare.isPending ? "Готовим предпросмотр…" : preview ? "Подготовить заново" : "Подготовить предпросмотр"}</Button>
        </form>
        {error && <div role="alert" className="space-y-1 text-sm text-destructive">
          <p>Не удалось выполнить действие. Проверьте код и повторите подготовку. Если данные изменились, нужен новый предпросмотр.</p>
          <details><summary className="cursor-pointer">Технические подробности</summary><p>{describeApiError(error, secrets)}</p></details>
        </div>}
        {preview && <section aria-label="Предпросмотр изменений" className="space-y-3 rounded-md border p-3">
          <h3 className="font-semibold">{redactKnownSecrets(preview.title, secrets)}</h3>
          <dl className="space-y-2">
            {preview.facts.map((fact, index) => <div key={index} className="text-sm">
              <dt className="font-medium">{redactKnownSecrets(fact.label, secrets)}</dt>
              <dd>Сейчас: {redactKnownSecrets(fact.before, secrets)} → После: {redactKnownSecrets(fact.after, secrets)}</dd>
            </div>)}
          </dl>
          {preview.missingEvidence.length > 0 && <div className="text-sm"><p className="font-medium">Чего не хватает</p>
            <ul className="list-disc space-y-1 pl-5">{preview.missingEvidence.map((item, index) => <li key={index}>{redactKnownSecrets(item, secrets)}</li>)}</ul>
          </div>}
          {Boolean(preview.bindingOptions?.length) && <fieldset className="space-y-2 rounded border p-3 text-sm">
            <legend className="font-medium">Какая запись уже учитывает эту комиссию?</legend>
            <p>Выберите соответствие и подготовьте предпросмотр заново. Сумма сама по себе не подтверждает совпадение.</p>
            {preview.bindingOptions?.map((option) => <label key={`${option.issueId}:${option.cashFlowId}`} className="flex items-start gap-2">
              <input type="radio" name={`review-binding-${option.issueId}`} disabled={busy || succeeded}
                checked={bindings.some((binding) => binding.issueId === option.issueId && binding.cashFlowId === option.cashFlowId)}
                onChange={() => { setBindings((previous) => [...previous.filter((binding) => binding.issueId !== option.issueId),
                  { issueId: option.issueId, cashFlowId: option.cashFlowId }]); setVersion((previous) => previous + 1); apply.reset(); }} />
              <span>{option.date} · {formatCurrency(option.amount, option.currency)} · {option.source === IMPORT_SOURCE_OBJ.IMPORT ? "Импорт" : "Сохранённая запись"}
                {option.label ? ` · ${redactKnownSecrets(option.label, secrets)}` : ""}</span>
            </label>)}
          </fieldset>}
          {!current && <p role="status" className="text-sm text-amber-800">Выбор изменился. Подготовьте новый предпросмотр.</p>}
          {preview.applyReady && preview.package && current && !succeeded && <Button type="button" disabled={busy}
            onClick={() => { if (preview.package) apply.mutate({ package: preview.package, acceptedDigest: preview.package.digest }); }}>
            {apply.isPending ? "Применяем изменения…" : "Применить перечисленные изменения"}
          </Button>}
        </section>}
      </>}
      {succeeded && <p role="status" className="text-sm text-emerald-700">{apply.data?.replayed ? "Этот пакет уже был применён. Вторых записей не создано." : "Изменения применены."}</p>}
      <Button type="button" variant="outline" disabled={busy} onClick={onClose}>{succeeded ? "Готово" : "Закрыть без изменений"}</Button>
    </div>
  </Modal>;
}
